import React, { useState, useEffect } from 'react';
import { FaArrowLeft, FaCheck, FaGraduationCap, FaBriefcase, FaMoneyBill, FaBuilding, FaUserTie } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';

const EducationInformation = ({ formData, onSubmit, onPrevious, loading, userId }) => {
    const [localData, setLocalData] = useState({
        education: formData.education || '',
        occupation: formData.occupation || '',
        monthaly_income: formData.monthaly_income || '',
        annual_income: formData.annual_income || '',
        employee_in: formData.employee_in || '',
        designation: formData.designation || ''
    });
    
    const [educationOptions, setEducationOptions] = useState([]);
    const [professionOptions, setProfessionOptions] = useState([]);
    const [loadingDropdowns, setLoadingDropdowns] = useState(true);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});

    // Income range options
    const incomeRanges = [
        { value: '0-10000', label: 'Rs0 - Rs10,000' },
        { value: '10000-20000', label: 'Rs10,000 - Rs20,000' },
        { value: '20000-30000', label: 'Rs20,000 - Rs30,000' },
        { value: '30000-40000', label: 'Rs30,000 - Rs40,000' },
        { value: '40000-50000', label: 'Rs40,000 - Rs50,000' },
        { value: '50000-60000', label: 'Rs50,000 - Rs60,000' },
        { value: '60000-70000', label: 'Rs60,000 - Rs70,000' },
        { value: '70000-80000', label: 'Rs70,000 - Rs80,000' },
        { value: '80000-90000', label: 'Rs80,000 - Rs90,000' },
        { value: '90000-100000', label: 'Rs90,000 - Rs1,00,000' },
        { value: '100000-150000', label: 'Rs1,00,000 - Rs1,50,000' },
        { value: '150000-200000', label: 'Rs1,50,000 - Rs2,00,000' },
        { value: '200000-300000', label: 'Rs2,00,000 - Rs3,00,000' },
        { value: '300000-500000', label: 'Rs3,00,000 - Rs5,00,000' },
        { value: '500000-1000000', label: 'Rs5,00,000 - Rs10,00,000' },
        { value: '1000000+', label: 'Rs10,00,000+' }
    ];

    // Fetch dropdown data
    useEffect(() => {
        fetchDropdowns();
    }, []);

    const fetchDropdowns = async () => {
        try {
            const response = await axios.get('https://app.bihebari.com.np/api/education/dropdowns');
            if (response.data.success) {
                setEducationOptions(response.data.data.education);
                setProfessionOptions(response.data.data.professions);
                console.log('Education dropdowns loaded:', response.data.data);
            }
        } catch (error) {
            console.error('Error fetching dropdowns:', error);
            toast.error('Error loading form data');
        } finally {
            setLoadingDropdowns(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Auto-calculate annual income when monthly income range changes
        if (name === 'monthaly_income') {
            let annualValue = '';
            if (value && value !== '') {
                // Extract the range and calculate approximate annual income
                if (value.includes('-')) {
                    const parts = value.split('-');
                    const minMonthly = parseInt(parts[0]);
                    const maxMonthly = parts[1] === '1000000+' ? 1000000 : parseInt(parts[1]);
                    const avgMonthly = (minMonthly + maxMonthly) / 2;
                    const annual = Math.round(avgMonthly * 12);
                    annualValue = annual.toLocaleString('en-IN');
                } else if (value === '1000000+') {
                    annualValue = '1,20,00,000+';
                }
            }
            
            setLocalData({ 
                ...localData, 
                [name]: value,
                annual_income: annualValue
            });
        } else {
            setLocalData({ ...localData, [name]: value });
        }
        
        // Mark field as touched
        setTouched({ ...touched, [name]: true });
        
        // Clear error for this field
        if (errors[name]) {
            setErrors({ ...errors, [name]: null });
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        setTouched({ ...touched, [name]: true });
        validateField(name, localData[name]);
    };

    const validateField = (fieldName, value) => {
        let error = '';
        
        switch (fieldName) {
            case 'education':
                if (!value || value === '') {
                    error = 'Education level is required';
                }
                break;
            case 'occupation':
                if (!value || value === '') {
                    error = 'Occupation is required';
                }
                break;
            case 'monthaly_income':
                if (!value || value === '') {
                    error = 'Monthly income range is required';
                }
                break;
            case 'employee_in':
                if (!value || value.trim() === '') {
                    error = 'Company/Organization name is required';
                } else if (value.length < 2) {
                    error = 'Company name must be at least 2 characters';
                } else if (value.length > 100) {
                    error = 'Company name must be less than 100 characters';
                }
                break;
            case 'designation':
                if (!value || value.trim() === '') {
                    error = 'Designation / Job Title is required';
                } else if (value.length < 2) {
                    error = 'Designation must be at least 2 characters';
                } else if (value.length > 100) {
                    error = 'Designation must be less than 100 characters';
                } else if (/[^a-zA-Z\s\-\.]/.test(value)) {
                    error = 'Only letters, spaces, hyphens, and dots are allowed';
                }
                break;
            default:
                break;
        }
        
        setErrors(prev => ({ ...prev, [fieldName]: error }));
        return error === '';
    };

    const validateForm = () => {
        const fieldsToValidate = ['education', 'occupation', 'monthaly_income', 'employee_in', 'designation'];
        let isValid = true;
        const newErrors = {};
        
        fieldsToValidate.forEach(field => {
            const value = localData[field];
            let error = '';
            
            switch (field) {
                case 'education':
                    if (!value || value === '') error = 'Education level is required';
                    break;
                case 'occupation':
                    if (!value || value === '') error = 'Occupation is required';
                    break;
                case 'monthaly_income':
                    if (!value || value === '') error = 'Monthly income range is required';
                    break;
                case 'employee_in':
                    if (!value || value.trim() === '') {
                        error = 'Company/Organization name is required';
                    } else if (value.length < 2) {
                        error = 'Company name must be at least 2 characters';
                    } else if (value.length > 100) {
                        error = 'Company name must be less than 100 characters';
                    }
                    break;
                case 'designation':
                    if (!value || value.trim() === '') {
                        error = 'Designation / Job Title is required';
                    } else if (value.length < 2) {
                        error = 'Designation must be at least 2 characters';
                    } else if (value.length > 100) {
                        error = 'Designation must be less than 100 characters';
                    } else if (/[^a-zA-Z\s\-\.]/.test(value)) {
                        error = 'Only letters, spaces, hyphens, and dots are allowed';
                    }
                    break;
                default:
                    break;
            }
            
            if (error) {
                isValid = false;
                newErrors[field] = error;
            }
        });
        
        setErrors(newErrors);
        
        // Mark all fields as touched
        const allTouched = {};
        fieldsToValidate.forEach(field => {
            allTouched[field] = true;
        });
        setTouched(allTouched);
        
        return isValid;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            const submissionData = {
                user_id: userId,
                education: localData.education || '',
                occupation: localData.occupation || '',
                monthaly_income: localData.monthaly_income || null,
                annual_income: localData.annual_income || null,
                employee_in: localData.employee_in || '',
                designation: localData.designation || ''
            };
            
            console.log('Submitting education data:', submissionData);
            onSubmit(submissionData);
        } else {
            toast.error('Please fill all required fields correctly');
        }
    };

    // Helper function to get selected option label
    const getSelectedEducationLabel = () => {
        const selected = educationOptions.find(opt => opt.name === localData.education);
        return selected ? selected.name : 'Not selected';
    };

    const getSelectedOccupationLabel = () => {
        const selected = professionOptions.find(opt => opt.name === localData.occupation);
        return selected ? selected.name : 'Not selected';
    };

    const getSelectedIncomeLabel = () => {
        const selected = incomeRanges.find(range => range.value === localData.monthaly_income);
        return selected ? selected.label : 'Not selected';
    };

    if (loadingDropdowns) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-center">
                    <div className="inline-block w-12 h-12 border-t-2 border-indigo-600 rounded-full animate-spin"></div>
                    <p className="mt-2 text-gray-600">Loading form data...</p>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex items-center space-x-2 mb-6">
                <FaGraduationCap className="text-indigo-600 text-xl" />
                <h2 className="text-2xl font-bold text-gray-800">Career & Professional Information</h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Education Dropdown */}
                <div>
                    <label className="form-label">
                        <FaGraduationCap className="inline mr-2" />
                        Education Level <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="education"
                        value={localData.education}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.education && touched.education ? 'input-error' : ''}`}
                    >
                        <option value="">Select Education Level</option>
                        {educationOptions.map((edu) => (
                            <option key={edu.id} value={edu.name}>
                                {edu.name}
                            </option>
                        ))}
                    </select>
                    {errors.education && touched.education && <p className="error-message">{errors.education}</p>}
                </div>
                
                {/* Occupation Dropdown */}
                <div>
                    <label className="form-label">
                        <FaBriefcase className="inline mr-2" />
                        Occupation / Profession <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="occupation"
                        value={localData.occupation}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.occupation && touched.occupation ? 'input-error' : ''}`}
                    >
                        <option value="">Select Occupation</option>
                        {professionOptions.map((prof) => (
                            <option key={prof.id} value={prof.name}>
                                {prof.name}
                            </option>
                        ))}
                    </select>
                    {errors.occupation && touched.occupation && <p className="error-message">{errors.occupation}</p>}
                </div>
                
                {/* Monthly Income Dropdown */}
                <div>
                    <label className="form-label">
                        <FaMoneyBill className="inline mr-2" />
                        Monthly Income Range (Rs) <span className="text-red-500">*</span>
                    </label>
                    <select
                        name="monthaly_income"
                        value={localData.monthaly_income}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.monthaly_income && touched.monthaly_income ? 'input-error' : ''}`}
                    >
                        <option value="">Select Monthly Income Range</option>
                        {incomeRanges.map((range) => (
                            <option key={range.value} value={range.value}>
                                {range.label}
                            </option>
                        ))}
                    </select>
                    {errors.monthaly_income && touched.monthaly_income && <p className="error-message">{errors.monthaly_income}</p>}
                    <p className="text-xs text-gray-500 mt-1">Select your monthly income range</p>
                </div>
                
                {/* Annual Income (Auto-calculated, read-only) */}
                <div>
                    <label className="form-label">
                        <FaMoneyBill className="inline mr-2" />
                        Annual Income (Rs)
                    </label>
                    <input
                        type="text"
                        name="annual_income"
                        value={localData.annual_income}
                        readOnly
                        className="form-input bg-gray-100 cursor-not-allowed"
                        placeholder="Auto-calculated from monthly income"
                    />
                    <p className="text-xs text-gray-500 mt-1">Auto-calculated (Monthly × 12)</p>
                </div>
                
                {/* Employee In / Company Name */}
                <div>
                    <label className="form-label">
                        <FaBuilding className="inline mr-2" />
                        Employee In / Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="employee_in"
                        value={localData.employee_in}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.employee_in && touched.employee_in ? 'input-error' : ''}`}
                        placeholder="Enter company/organization name"
                    />
                    {errors.employee_in && touched.employee_in && <p className="error-message">{errors.employee_in}</p>}
                    <p className="text-xs text-gray-500 mt-1">Enter your company or organization name</p>
                </div>
                
                {/* Designation / Job Title - Text Input */}
                <div>
                    <label className="form-label">
                        <FaUserTie className="inline mr-2" />
                        Designation / Job Title <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="designation"
                        value={localData.designation}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`form-input ${errors.designation && touched.designation ? 'input-error' : ''}`}
                        placeholder="Enter your job title or designation"
                    />
                    {errors.designation && touched.designation && <p className="error-message">{errors.designation}</p>}
                    <p className="text-xs text-gray-500 mt-1">Enter your job title (e.g., Software Engineer, Manager, etc.)</p>
                </div>
            </div>
            
            {/* Summary Section */}
            <div className="mt-8 p-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl border border-indigo-100">
                <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center">
                    <FaCheck className="text-green-600 mr-2" />
                    Registration Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                        <p><strong className="text-gray-700">Name:</strong> <span className="text-gray-600">{formData.name || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Date of Birth:</strong> <span className="text-gray-600">{formData.dob || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Gender:</strong> <span className="text-gray-600">{formData.gender || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Marital Status:</strong> <span className="text-gray-600">{formData.marital || 'Not provided'}</span></p>
                    </div>
                    <div className="space-y-2">
                        <p><strong className="text-gray-700">Caste:</strong> <span className="text-gray-600">{formData.caste || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Gotra:</strong> <span className="text-gray-600">{formData.gotra || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Food Preference:</strong> <span className="text-gray-600">{formData.eat_status || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Registration Amount:</strong> <span className="text-green-600 font-semibold">Rs 4,000</span></p>
                    </div>
                </div>
                <div className="mt-4 pt-4 border-t border-indigo-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <p><strong className="text-gray-700">Education:</strong> <span className="text-gray-600">{getSelectedEducationLabel()}</span></p>
                        <p><strong className="text-gray-700">Occupation:</strong> <span className="text-gray-600">{getSelectedOccupationLabel()}</span></p>
                        <p><strong className="text-gray-700">Monthly Income:</strong> <span className="text-gray-600">{getSelectedIncomeLabel()}</span></p>
                        <p><strong className="text-gray-700">Company:</strong> <span className="text-gray-600">{localData.employee_in || 'Not provided'}</span></p>
                        <p><strong className="text-gray-700">Designation:</strong> <span className="text-gray-600">{localData.designation || 'Not provided'}</span></p>
                    </div>
                </div>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex justify-between pt-4">
                <button
                    type="button"
                    onClick={onPrevious}
                    disabled={loading}
                    className="btn-secondary flex items-center space-x-2"
                >
                    <FaArrowLeft />
                    <span>Previous</span>
                </button>
                <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary flex items-center space-x-2"
                >
                    {loading ? (
                        <>
                            <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin"></div>
                            <span>Saving...</span>
                        </>
                    ) : (
                        <>
                            <span>Next: Family Information</span>
                            <span>→</span>
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default EducationInformation;